/**
 * ControlPanel - 3D模型控制面板组件
 * 用于节点显隐控制
 */

import { useState, useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import styles from './ControlPanel.module.css';

export default function ControlPanel({
    nodeList = [],
    hiddenNodes = [],
    onToggleNode,
    onResetVisibility,
}) {
    const { t } = useLanguage();
    const [isExpanded, setIsExpanded] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSize, setFilterSize] = useState('all'); // all, large, medium, small

    // 过滤节点列表
    const filteredNodes = useMemo(() => {
        return nodeList.filter(node => {
            // 搜索过滤
            if (searchTerm && !node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
            // 大小过滤
            if (filterSize !== 'all' && node.sizeLabel !== filterSize) {
                return false;
            }
            return true;
        });
    }, [nodeList, searchTerm, filterSize]);

    const hiddenCount = hiddenNodes.length;

    return (
        <div className={`${styles.panel} ${isExpanded ? styles.expanded : styles.collapsed}`}>
            {/* Toggle Button */}
            <button
                className={styles.toggleBtn}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                {isExpanded ? '›' : '‹'}
            </button>

            {isExpanded && (
                <div className={styles.content}>
                    {/* Header */}
                    <div className={styles.header}>
                        <h3 className={styles.title}>{t('viewer3d.control.title')}</h3>
                        {hiddenCount > 0 && (
                            <button
                                className={styles.resetBtn}
                                onClick={onResetVisibility}
                            >
                                {t('viewer3d.control.reset')} ({hiddenCount})
                            </button>
                        )}
                    </div>

                    {/* Search */}
                    <div className={styles.search}>
                        <input
                            type="text"
                            placeholder={t('viewer3d.control.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                    </div>

                    {/* Size Filter */}
                    <div className={styles.filters}>
                        {['all', 'large', 'medium', 'small'].map(size => (
                            <button
                                key={size}
                                className={`${styles.filterBtn} ${filterSize === size ? styles.active : ''}`}
                                onClick={() => setFilterSize(size)}
                            >
                                {size === 'all' ? t('viewer3d.control.sizeAll') :
                                 size === 'large' ? t('viewer3d.control.sizeLarge') :
                                 size === 'medium' ? t('viewer3d.control.sizeMedium') :
                                 t('viewer3d.control.sizeSmall')}
                            </button>
                        ))}
                    </div>

                    {/* Node List */}
                    <div className={styles.nodeList}>
                        {filteredNodes.map((node) => {
                            const isHidden = hiddenNodes.includes(node.name);
                            return (
                                <div
                                    key={node.name}
                                    className={`${styles.nodeItem} ${isHidden ? styles.hidden : ''}`}
                                    onClick={() => onToggleNode(node.name)}
                                >
                                    <span className={styles.nodeStatus}>
                                        {isHidden ? '○' : '●'}
                                    </span>
                                    <span className={styles.nodeName}>
                                        {node.name}
                                    </span>
                                    <span className={styles.nodeSize}>
                                        {node.sizeLabel === 'large' ? t('viewer3d.control.sizeLarge') :
                                         node.sizeLabel === 'medium' ? t('viewer3d.control.sizeMedium') :
                                         t('viewer3d.control.sizeSmall')}
                                    </span>
                                </div>
                            );
                        })}
                        {filteredNodes.length === 0 && (
                            <div className={styles.emptyState}>
                                {t('viewer3d.control.noParts')}
                            </div>
                        )}
                    </div>

                    {/* Stats */}
                    <div className={styles.stats}>
                        <span>{t('viewer3d.control.total')}: {nodeList.length}</span>
                        <span>{t('viewer3d.control.showing')}: {filteredNodes.length}</span>
                        <span>{t('viewer3d.control.hiddenCount')}: {hiddenCount}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
